// Correct Score Probability Matrix — the pure model behind the widget.
//
// This is the allowed "tiny calc that scales dummy data to the fixture"
// exception (CLAUDE.md → Dummy data): a Poisson model turns per-team
// attack/defence ratings into a 5×5 scoreline probability grid. When app-api
// arrives it will send the pre-computed grid and this calc is replaced — the
// component renders the same shape either way.
//
// Ported verbatim from the legacy renderer (renderers-match.js) + data.js so
// the React widget and the legacy innerHTML produce identical numbers; the
// visual A/B test guards that they stay in step until the legacy copy is gone.

/** Per-team [attack, defence] ratings. Dummy until app-api supplies the grid. */
const CS_TEAMS: Record<string, readonly [number, number]> = {
  Arsenal: [4.5, 4.5],
  Liverpool: [4.8, 4.5],
  "Manchester City": [4.7, 4.5],
  Chelsea: [3.8, 3.5],
  Tottenham: [3.8, 3.0],
  Newcastle: [3.5, 3.5],
  "Aston Villa": [3.5, 3.0],
  Brighton: [3.2, 3.0],
  Fulham: [2.8, 3.0],
  Brentford: [3.0, 2.8],
  Bournemouth: [2.8, 2.5],
  "Manchester United": [2.8, 2.5],
  "West Ham": [2.5, 2.5],
  "Crystal Palace": [2.5, 2.8],
  Wolves: [2.3, 2.5],
  Everton: [2.0, 2.0],
  Leicester: [2.2, 2.0],
  "Nottingham Forest": [2.5, 2.8],
  Ipswich: [1.8, 2.0],
  Southampton: [1.5, 1.5],
  Burnley: [1.5, 1.8],
};

/** Neutral fallback for a team not in the ratings table. */
const CS_DEFAULT: readonly [number, number] = [3.0, 3.0];

/** Goals shown per axis (0–CS_MAX_GOALS inclusive → a 5×5 grid). */
export const CS_MAX_GOALS = 4;

/** Poisson P(k; lambda). */
function poisson(lambda: number, k: number): number {
  let p = Math.exp(-lambda);
  for (let i = 1; i <= k; i++) p *= lambda / i;
  return p;
}

export interface CorrectScoreGrid {
  /** grid[home][away] = probability (0–1) of that exact scoreline. 5×5. */
  grid: number[][];
  /** Largest cell probability — scales the heatmap colour. */
  maxP: number;
}

/**
 * The 5×5 scoreline probability grid for a fixture. Unknown teams fall back to
 * a neutral rating. `lH`/`lA` are the expected goals for each side (home gets a
 * 1.3× advantage), exactly as the legacy renderer computed them.
 */
export function correctScoreGrid(
  homeName: string,
  awayName: string
): CorrectScoreGrid {
  const [hAtt, hDef] = CS_TEAMS[homeName.trim()] ?? CS_DEFAULT;
  const [aAtt, aDef] = CS_TEAMS[awayName.trim()] ?? CS_DEFAULT;
  const lH = (hAtt * 1.3) / aDef;
  const lA = aAtt / hDef;

  const grid: number[][] = [];
  let maxP = 0;
  for (let h = 0; h <= CS_MAX_GOALS; h++) {
    const row: number[] = [];
    for (let a = 0; a <= CS_MAX_GOALS; a++) {
      const p = poisson(lH, h) * poisson(lA, a);
      row.push(p);
      if (p > maxP) maxP = p;
    }
    grid.push(row);
  }
  return { grid, maxP };
}

/**
 * Heatmap colour for a cell, interpolating dark → bright green by p/maxP.
 * Matches the legacy `cellBg`.
 */
export function heatmapCellColor(p: number, maxP: number): string {
  const t = maxP === 0 ? 0 : p / maxP;
  const r = Math.round(26 + t * (46 - 26));
  const g = Math.round(50 + t * (204 - 50));
  const b = Math.round(40 + t * (113 - 40));
  return `rgb(${r},${g},${b})`;
}

/** Cell label: probability as a whole-number percent, e.g. "12%". */
export function scoreLabelPct(p: number): string {
  return Math.round(p * 100) + "%";
}
